import { Injectable } from '@angular/core';
import { Observable, ReplaySubject } from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Duration } from 'moment';
import * as moment from 'moment/moment';
import { SessionService } from './session.service';
import { SocketService } from './socket.service';
import { Router } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { MixpanelService } from './mixpanel.service';
import { MixpanelEvents } from '../models/mixpanelEvents';

export interface ITimeTrackingStatus {
	enable: boolean;
	key: string | number;
}

export interface ITimeTracker {
	time: Duration;
	running: boolean;
	timerId?: number;
	name?: string;
	link?: any;
	isUpdating?: boolean;
}

export interface ITimeTrackingRecord {
	created_at: string;
	execution: number;
	id: number;
	is_running: boolean;
	time: string;
	updated_at: string;
	planogram_name: string;
	planogram_id: number;
	active_planogram_id: number;
	plan_id: number;
	execution_done: boolean;
}

export interface ITimeTrackingDelete {
	id: number;
	execution_id: number;
}

@Injectable({
	providedIn: 'root'
})
export class TimeTrackingService {
	private _updateTrackerLookup$: ReplaySubject<any> = new ReplaySubject<any>();
	initTrackersPromise: Promise<void>;
	someTimerActive: boolean = false;
	timerIntervals: Map<string | number, any> = new Map<string | number, any>();
	timers: Map<string | number, ITimeTracker> = new Map<string | number, ITimeTracker>();
	timeTrackersInitSubject: ReplaySubject<void> = new ReplaySubject<void>(1);

	constructor(
		private http: HttpClient,
		private router: Router,
		private sessionService: SessionService,
		private socketService: SocketService,
		private mixPanelService: MixpanelService
	) {
		if (this.sessionService.isFramePage) return;
		// щоб сокети оброблялись тільки для менеджера
		if (this.sessionService.defaults.isManager && this.sessionService.defaults.planogramId) {
			this.socketService.timeTracker.subscribe((tracker: ITimeTrackingRecord) => {
				const alreadyTimer = this.timers.get(tracker.execution);
				if (alreadyTimer && alreadyTimer.running === tracker.is_running) return;
				this.setTimeTracking(tracker);
			});
			this.socketService.timeTrackerDelete.subscribe((tracker: ITimeTrackingDelete) =>
				this.deleteTracker(tracker.execution_id)
			);
			this.socketService.timeTrackingDeleteAll.subscribe(() => {
				this.timers.forEach((timer: ITimeTracker, key: string | number) => {
					this.deleteTracker(key);
				});
			});
			this.initTrackersPromise = new Promise(resolve => {
				this.timeTrackersInitSubject.subscribe(() => resolve());
			});
			this._updateTrackerLookup$
				.pipe(
					switchMap((params) => {
						const id: number = params.id;
						delete params.id;
						return this.http.patch<ITimeTrackingRecord>(`api/layout/time-tracking-records/${id}/`, params);
					})
				)
				.subscribe((tracker: ITimeTrackingRecord) => this.timers.get(tracker.execution).isUpdating = false);
		}
	}

	private _setInterval(key: string | number): void {
		this.timerIntervals.set(key, setInterval(() => {
			this.timers.get(key).time.add({ s: 1 });
			this.setTimer(key, this.timers.get(key).time);
		}, 1000));
	}

	private _setSomeTrackerActive(): void {
		this.someTimerActive = false;
		this.timers.forEach((value: ITimeTracker) => {
			if (!!value.time.asMilliseconds()) this.someTimerActive = true;
		});
	}

	private _startTimeTracking(key: string | number): Observable<ITimeTrackingRecord> {
		return this.http.post<ITimeTrackingRecord>('api/layout/time-tracking-records/', {
			execution: key,
			is_running: true
		});
	}

	private _updateTimeTracking(key: string | number) {
		const timer = this.timers.get(key);
		if (!timer.timerId) return;
		// calculate hours, because moment returned hours in days format (if hours more 24 > '00:23:23')
		const hours: string = Math.floor(timer.time.asHours()).toString(),
			minutes: string = moment.utc(timer.time.asMilliseconds()).format('mm:ss'),
			time: string = hours + ':' + minutes;

		timer.isUpdating = true;
		this._updateTrackerLookup$.next({
			id: timer.timerId,
			is_running: timer.running,
			time: time
		});
	}

	deleteTracker(key: string | number): void {
		clearInterval(this.timerIntervals.get(key));
		this.timerIntervals.delete(key);
		this.timers.delete(key);
		this._setSomeTrackerActive();
	}

	getAllTimers(params: { execution_done: boolean, show_all: boolean }): Observable<Array<ITimeTrackingRecord>> {
		return this.http.get<Array<ITimeTrackingRecord>>('/api/layout/time-tracking-records/', {
			params: new HttpParams({
				fromObject: params
			})
		});
	}

	setTimer(key: string | number, time: Duration, timerName: string = '', link: string = ''): void {
		if (this.timers.has(key)) {
			this.timers.get(key).time = time;
		} else {
			this.timers.set(key, {
				time: time,
				running: false,
				name: timerName,
				link: link
			});
		}
		this._setSomeTrackerActive();
	}

	setTimeTracking(timer: ITimeTrackingRecord): void {
		let timeValueMilliseconds = 0;
		if (timer.is_running) {
			timeValueMilliseconds += (new Date).getTime() - (new Date(timer.updated_at)).getTime();
		}
		if (timer.time) {
			timeValueMilliseconds += (moment.duration(timer.time)).asMilliseconds();
		}
		const link: string = this.router.serializeUrl(
			this.router.createUrlTree(
				this.sessionService.getLink(['notifications', `${timer.active_planogram_id},${timer.planogram_id}`]),
				{ queryParams: { planogram: timer.plan_id } }
			)
		);
		this.setTimer(timer.execution, moment.duration({ ms: timeValueMilliseconds }), timer.planogram_name, link);
		this.timers.get(timer.execution).running = !!timer.is_running;
		this.timers.get(timer.execution).timerId = timer.id;
		if (!!timer.is_running) {
			this._setInterval(timer.execution);
		} else {
			clearInterval(this.timerIntervals.get(timer.execution));
			this.timerIntervals.delete(timer.execution);
		}
	}

	startTracking(key: string | number): void {
		this.timers.get(key).running = true;
		this._setInterval(key);
		this.mixPanelService.trackPage(MixpanelEvents.TimeTrackerStart);
		this._startTimeTracking(key).subscribe((response: ITimeTrackingRecord) => this.timers.get(key).timerId = response.id);
	}

	timerContinue(key: string | number): void {
		this._setInterval(key);
		this.timers.get(key).running = true;
		this.mixPanelService.trackPage(MixpanelEvents.TimeTrackerContinue);
		this._updateTimeTracking(key);
	}

	timerPause(key: string | number): void {
		clearInterval(this.timerIntervals.get(key));
		this.timerIntervals.delete(key);
		this.timers.get(key).running = false;
		this.mixPanelService.trackPage(MixpanelEvents.TimeTrackerPause);
		this._updateTimeTracking(key);
	}
}
